import React, { useState, useMemo, useCallback } from 'react';
import { liveOrders, needsRefundDecision } from './lib/liveOrders';
import { TestOrdersNote } from './RentalDashboard';
import RefundDialog from './components/orderPipeline/RefundDialog';
import { taxLines } from './lib/taxLines';
import axios from './lib/axiosClient';

const C = {
  bg:'#1a1c28', card:'#252838', border:'#2e3150', text:'#e2e8f0',
  muted:'#64748b', accent:'#f97316', green:'#4ade80', red:'#f87171',
  blue:'#60a5fa', amber:'#fbbf24', purple:'#a78bfa',
};
const PAYMENT_COLOR = {
  paid:            { bg:'#14532d',  color:'#4ade80' },
  payment_due:     { bg:'#3b1a00',  color:'#fbbf24' },
  partially_paid:  { bg:'#1a1a00',  color:'#d97706' },
  overpaid:        { bg:'#1a0a2e',  color:'#a78bfa' },
  process_deposit: { bg:'#0a1a3b',  color:'#60a5fa' },
};
const PAYMENT_LABEL = { paid:'Paid', payment_due:'Due', partially_paid:'Partial', overpaid:'Overpaid', process_deposit:'Deposit' };
const METHOD_ICON   = { cash:'💵', card:'💳', check:'📄', venmo:'💜', zelle:'💙', stripe:'⚡', other:'💰' };
const fmt     = (n) => `$${Number(n||0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g,',')}`;
const fmtDate = (d) => d ? new Date(d+'T12:00:00').toLocaleDateString('en-US',{month:'short',day:'numeric',year:'numeric'}) : '—';
const fmtTime = (d) => d ? new Date(d).toLocaleString('en-US',{month:'short',day:'numeric',hour:'numeric',minute:'2-digit'}) : '—';
const today   = () => new Date().toISOString().slice(0,10);

const S = {
  wrap:  { fontFamily:"'DM Sans',Arial,sans-serif", padding:'24px 20px' },
  card:  { background:C.card, border:`1px solid ${C.border}`, borderRadius:12, padding:20, marginBottom:16 },
  stat:  { background:'#181928', borderRadius:10, padding:'14px 18px', flex:1, minWidth:110 },
  input: { background:'#181928', border:`1px solid ${C.border}`, borderRadius:8, padding:'8px 12px', color:C.text, fontSize:13, outline:'none' },
  pill:  (s) => ({ padding:'2px 8px', borderRadius:4, fontSize:11, fontWeight:700, ...(PAYMENT_COLOR[s]||PAYMENT_COLOR.payment_due) }),
  btn:   (v) => ({ padding:v==='sm'?'5px 10px':'8px 14px', borderRadius:6, border:'none', fontWeight:600, fontSize:12, cursor:'pointer',
    background: v==='green'?'#14532d':v==='danger'?'#3b0a0a':v==='ghost'?'transparent':v==='orange'?'rgba(249,115,22,0.15)':'#2e3150',
    color: v==='green'?C.green:v==='danger'?C.red:v==='ghost'?C.muted:v==='orange'?C.accent:C.text }),
  th:    { padding:'10px 14px', textAlign:'left', fontSize:11, color:C.muted, fontWeight:600, textTransform:'uppercase', letterSpacing:'.4px', borderBottom:`1px solid ${C.border}`, whiteSpace:'nowrap' },
  td:    { padding:'11px 14px', fontSize:13, borderBottom:`1px solid #0a0b12`, verticalAlign:'middle' },
  lbl:   { fontSize:11, fontWeight:600, color:C.muted, textTransform:'uppercase', letterSpacing:'.4px', display:'block', marginBottom:5, marginTop:14 },
  inp:   { padding:'10px 12px', borderRadius:8, border:'1px solid #3a3f60', background:'#181928', color:C.text, fontSize:13, width:'100%', boxSizing:'border-box', outline:'none' },
};

export default function RentalBilling({ orders = [], onOrderUpdate }) {
  const [search,    setSearch]    = useState('');
  const [filter,    setFilter]    = useState('all');
  const [billingTab, setBillingTab] = useState('table');  // table | aging
  const [dateRange, setDateRange] = useState({ from:'', to:'' });
  const [expandId,  setExpandId]  = useState(null);
  const [ledger,    setLedger]    = useState({});
  const [loadingL,  setLoadingL]  = useState({});
  const [collectModal, setCollectModal] = useState(null);
  const [refundModal,  setRefundModal]  = useState(null);
  const [payLinkModal, setPayLinkModal] = useState(null);
  const [selected,  setSelected]  = useState(new Set());   // bulk select
  const [bulkSending, setBulkSending] = useState(false);

  const loadLedger = useCallback(async (id) => {
    if (ledger[id]) return;
    setLoadingL(p=>({...p,[id]:true}));
    try { const r = await axios.get(`/api/rentals/orders/${id}/ledger`); setLedger(p=>({...p,[id]:r.data.ledger||[]})); } catch {}
    setLoadingL(p=>({...p,[id]:false}));
  },[ledger]);

  const refreshLedger = async (id) => {
    setLoadingL(p=>({...p,[id]:true}));
    try { const r = await axios.get(`/api/rentals/orders/${id}/ledger`); setLedger(p=>({...p,[id]:r.data.ledger||[]})); } catch {}
    setLoadingL(p=>({...p,[id]:false}));
  };

  const handleExpand = (id) => { if(expandId===id){setExpandId(null);}else{setExpandId(id);loadLedger(id);} };
  const clearLedgerCache = (id) => setLedger(p=>({...p,[id]:undefined}));

  // Every figure on this tab uses the shared live-order rule (lib/liveOrders):
  // cancelled and parallel-run test orders never count as billed or due.
  const live = useMemo(()=>liveOrders(orders),[orders]);
  // Cancelled orders that still hold money: shown separately, refund only.
  const refundDecisions = useMemo(()=>orders.filter(needsRefundDecision),[orders]);
  const depositDue = (o) => Number(o.deposit_amount||0) > 0 && Number(o.amount_paid||0) < Number(o.deposit_amount||0) && o.payment_status !== 'paid';
  const stats = useMemo(()=>{
    const rel = live;
    const totalBilled    = rel.reduce((s,o)=>s+Number(o.total||0),0);
    const totalCollected = rel.reduce((s,o)=>s+Number(o.amount_paid||0),0);
    const totalDue       = rel.filter(o=>['payment_due','partially_paid'].includes(o.payment_status)).reduce((s,o)=>s+Math.max(0,Number(o.total||0)-Number(o.amount_paid||0)),0);
    const dueCount       = rel.filter(o=>['payment_due','partially_paid'].includes(o.payment_status)).length;
    const overdueCount   = rel.filter(o=>['payment_due','partially_paid'].includes(o.payment_status)&&o.to_date&&o.to_date<today()).length;
    return { totalBilled, totalCollected, totalDue, dueCount, overdueCount };
  },[live]);

  const filtered = useMemo(()=>live.filter(o=>{
    if(filter==='paid'    && o.payment_status!=='paid') return false;
    if(filter==='due'     && !['payment_due','partially_paid'].includes(o.payment_status)) return false;
    if(filter==='overdue' && !([ 'payment_due','partially_paid'].includes(o.payment_status)&&o.to_date&&o.to_date<today())) return false;
    // "Deposit": the booking deposit has not been fully collected yet (the
    // old 'process_deposit' status was never written by anything).
    if(filter==='deposit' && !depositDue(o)) return false;
    if(dateRange.from&&o.from_date<dateRange.from) return false;
    if(dateRange.to&&o.from_date>dateRange.to) return false;
    if(search){const q=search.toLowerCase();return(o.client_name||'').toLowerCase().includes(q)||(o.client_email||'').toLowerCase().includes(q)||String(o.id).includes(q);}
    return true;
  }).sort((a,b)=>{
    const ad=['payment_due','partially_paid'].includes(a.payment_status)?0:1;
    const bd=['payment_due','partially_paid'].includes(b.payment_status)?0:1;
    if(ad!==bd)return ad-bd;
    return(b.created_at||'').localeCompare(a.created_at||'');
  }),[live,filter,search,dateRange]);

  const pct = stats.totalBilled>0 ? Math.round(stats.totalCollected/stats.totalBilled*100) : 0;

  return (
    <div style={S.wrap}>
      <div style={{marginBottom:20}}>
        <div style={{fontSize:22,fontWeight:800,color:C.text,marginBottom:4}}>💳 Billing & Payments</div>
        <div style={{fontSize:13,color:C.muted}}>Full audit trail · payment links · receipts · refunds</div>
        <TestOrdersNote rows={orders} />
      </div>

      {/* Stats */}
      <div style={{display:'flex',gap:10,marginBottom:14,flexWrap:'wrap'}}>
        {[
          {val:fmt(stats.totalBilled),    lbl:'Total Billed',    color:C.text,    b:''},
          {val:fmt(stats.totalCollected), lbl:'Collected',       color:C.green,   b:C.green},
          {val:fmt(stats.totalDue),       lbl:'Outstanding',     color:C.amber,   b:C.amber},
          {val:pct+'%',                   lbl:'Collection Rate', color:C.blue,    b:C.blue},
          {val:stats.dueCount,            lbl:'Unpaid Orders',   color:C.red,     b:''},
          {val:stats.overdueCount,        lbl:'Overdue',         color:'#fb923c', b:'#fb923c'},
        ].map(({val,lbl,color,b})=>(
          <div key={lbl} style={{...S.stat,borderLeft:b?`2px solid ${b}`:'none'}}>
            <div style={{fontSize:22,fontWeight:800,color,lineHeight:1}}>{val}</div>
            <div style={{fontSize:11,color:C.muted,marginTop:4}}>{lbl}</div>
          </div>
        ))}
      </div>

      {/* Progress bar */}
      <div style={{...S.card,padding:'14px 20px',marginBottom:14}}>
        <div style={{display:'flex',justifyContent:'space-between',marginBottom:8,fontSize:12,color:C.muted}}>
          <span>Collection progress</span>
          <span style={{color:C.text,fontWeight:700}}>{pct}%</span>
        </div>
        <div style={{background:'#2e3150',borderRadius:6,height:8,overflow:'hidden'}}>
          <div style={{width:`${pct}%`,height:'100%',background:`linear-gradient(90deg,${C.green},#22c55e)`,borderRadius:6,transition:'width .5s'}}/>
        </div>
        <div style={{display:'flex',justifyContent:'space-between',marginTop:6,fontSize:11,color:C.muted}}>
          <span style={{color:C.green}}>{fmt(stats.totalCollected)} collected</span>
          <span style={{color:C.amber}}>{fmt(stats.totalDue)} outstanding</span>
        </div>
      </div>

      {/* Tabs */}
      <div style={{display:'flex',gap:4,marginBottom:14}}>
        {[['table','📋 Payments'],['aging','⏳ Aging Report']].map(([t,l])=>(
          <button key={t} onClick={()=>setBillingTab(t)}
            style={{padding:'7px 18px',borderRadius:8,border:`1px solid ${billingTab===t?C.accent:'#2e3150'}`,background:billingTab===t?'rgba(249,115,22,0.08)':'transparent',color:billingTab===t?C.accent:C.muted,fontSize:12,cursor:'pointer',fontWeight:billingTab===t?700:400}}>
            {l}
          </button>
        ))}
      </div>

      {/* Aging Report */}
      {billingTab==='aging' && (()=>{
        const now = new Date();
        const due = live.filter(o => ['payment_due','partially_paid'].includes(o.payment_status) && o.to_date && o.to_date < today());
        const bucket = (o) => {
          const days = Math.floor((now - new Date(o.to_date)) / 86400000);
          if (days <= 7)  return '1–7 days';
          if (days <= 14) return '8–14 days';
          if (days <= 30) return '15–30 days';
          return '30+ days';
        };
        const buckets = { '1–7 days':[], '8–14 days':[], '15–30 days':[], '30+ days':[] };
        due.forEach(o => buckets[bucket(o)].push(o));
        const colors = { '1–7 days':'#fbbf24', '8–14 days':'#f97316', '15–30 days':'#ef4444', '30+ days':'#7f1d1d' };
        const totalDue = due.reduce((s,o)=>s+Math.max(0,Number(o.total||0)-Number(o.amount_paid||0)),0);

        return (
          <div>
            {/* Summary */}
            <div style={{display:'grid',gridTemplateColumns:'repeat(4,1fr)',gap:10,marginBottom:16}}>
              {Object.entries(buckets).map(([bkt, bOrders])=>{
                const amt = bOrders.reduce((s,o)=>s+Math.max(0,Number(o.total||0)-Number(o.amount_paid||0)),0);
                return (
                  <div key={bkt} style={{background:'#252838',border:`1px solid ${colors[bkt]}33`,borderTop:`3px solid ${colors[bkt]}`,borderRadius:10,padding:'14px 16px'}}>
                    <div style={{fontSize:11,color:colors[bkt],fontWeight:700,marginBottom:6}}>{bkt} overdue</div>
                    <div style={{fontSize:22,fontWeight:800,color:colors[bkt]}}>{fmt(amt)}</div>
                    <div style={{fontSize:11,color:C.muted,marginTop:4}}>{bOrders.length} order{bOrders.length!==1?'s':''}</div>
                  </div>
                );
              })}
            </div>
            <div style={{...S.card,padding:'10px 16px',marginBottom:10,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
              <span style={{fontSize:13,fontWeight:700,color:C.red}}>Total Overdue: {fmt(totalDue)}</span>
              <button style={{...S.btn('primary'),fontSize:12}}
                onClick={async()=>{
                  if(!window.confirm(`Send payment links to all ${due.length} overdue clients?`)) return;
                  setBulkSending(true);
                  let ok=0,fail=0;
                  for(const o of due){
                    try{await axios.post(`/api/rentals/orders/${o.id}/payment-link`,{payment_type:'balance',send_sms:!!(o.client_phone),send_email:!!(o.client_email)});ok++;}
                    catch{fail++;}
                  }
                  setBulkSending(false);
                  alert(`Sent ${ok} links${fail>0?`, ${fail} failed`:''}`);
                  onOrderUpdate?.();
                }}>
                {bulkSending?'Sending...':'🔗 Send Links to All Overdue'}
              </button>
            </div>
            {/* Aging table */}
            <div style={{...S.card,padding:0,overflow:'hidden'}}>
              <table style={{width:'100%',borderCollapse:'collapse'}}>
                <thead><tr style={{background:'#181928'}}>
                  {['#','Client','Due Date','Days Overdue','Total','Collected','Balance','Bucket',''].map(h=>(
                    <th key={h} style={S.th}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {due.sort((a,b)=>a.to_date.localeCompare(b.to_date)).map(o=>{
                    const days    = Math.floor((now - new Date(o.to_date)) / 86400000);
                    const balance = Math.max(0, Number(o.total||0)-Number(o.amount_paid||0));
                    const bkt     = bucket(o);
                    return (
                      <tr key={o.id} onMouseEnter={e=>e.currentTarget.style.background='#2a2d40'} onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                        <td style={S.td}><span style={{color:C.muted,fontSize:11}}>#{o.id}</span></td>
                        <td style={S.td}><span style={{fontWeight:600,color:C.text}}>{o.client_name}</span></td>
                        <td style={S.td}><span style={{color:C.muted,fontSize:11}}>{o.to_date}</span></td>
                        <td style={S.td}><span style={{color:colors[bkt],fontWeight:700}}>{days}d</span></td>
                        <td style={S.td}><span style={{color:C.text}}>{fmt(o.total)}</span></td>
                        <td style={S.td}><span style={{color:C.green}}>{fmt(o.amount_paid)}</span></td>
                        <td style={S.td}><span style={{color:C.red,fontWeight:700}}>{fmt(balance)}</span></td>
                        <td style={S.td}><span style={{fontSize:10,background:colors[bkt]+'22',color:colors[bkt],padding:'2px 8px',borderRadius:4,fontWeight:700}}>{bkt}</span></td>
                        <td style={S.td}>
                          <button style={{...S.btn('primary'),fontSize:10,padding:'4px 10px'}}
                            onClick={()=>axios.post(`/api/rentals/orders/${o.id}/payment-link`,{payment_type:'balance',send_sms:!!(o.client_phone),send_email:!!(o.client_email)}).then(()=>alert('Link sent!')).catch(()=>alert('Failed'))}>
                            🔗 Send
                          </button>
                        </td>
                      </tr>
                    );
                  })}
                  {due.length===0&&<tr><td colSpan={9} style={{...S.td,textAlign:'center',padding:40,color:C.muted}}>No overdue orders 🎉</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}

      {billingTab==='table' && (<>

      {/* Bulk actions */}
      {selected.size > 0 && (
        <div style={{...S.card,padding:'10px 16px',marginBottom:10,display:'flex',alignItems:'center',gap:12,background:'rgba(249,115,22,0.06)',border:`1px solid ${C.accent}44`}}>
          <span style={{fontSize:13,color:C.accent,fontWeight:700}}>{selected.size} selected</span>
          <button style={{...S.btn('primary'),fontSize:12}}
            disabled={bulkSending}
            onClick={async()=>{
              if(!window.confirm(`Send payment links to ${selected.size} clients?`)) return;
              setBulkSending(true);
              let ok=0,fail=0;
              for(const id of selected){
                const o = orders.find(x=>x.id===id);
                if(!o) continue;
                try{await axios.post(`/api/rentals/orders/${id}/payment-link`,{payment_type:'balance',send_sms:!!(o.client_phone),send_email:!!(o.client_email)});ok++;}
                catch{fail++;}
              }
              setBulkSending(false);
              setSelected(new Set());
              alert(`Sent ${ok} links${fail>0?`, ${fail} failed`:''}`);
              onOrderUpdate?.();
            }}>
            {bulkSending?'Sending...':'🔗 Send Payment Links'}
          </button>
          <button style={{...S.btn('ghost'),fontSize:12}} onClick={()=>setSelected(new Set())}>Clear</button>
        </div>
      )}

      {/* Cancelled orders that still hold money */}
      {billingTab==='table' && refundDecisions.length>0 && (
        <div data-testid="refund-decisions" style={{...S.card,border:'1px solid rgba(248,113,113,0.4)'}}>
          <div style={{fontSize:13,fontWeight:700,color:C.red,marginBottom:4}}>↩ Needs refund decision ({refundDecisions.length})</div>
          <div style={{fontSize:12,color:C.muted,marginBottom:10}}>These orders were cancelled after money was collected. They are excluded from every total above; refund or record the decision here. Collecting more on a cancelled order is not possible.</div>
          {refundDecisions.map(o=>(
            <div key={o.id} style={{display:'flex',alignItems:'center',gap:12,padding:'8px 0',borderTop:`1px solid ${C.border}`,fontSize:13}}>
              <span style={{fontFamily:'monospace',color:C.muted}}>{o.order_number||'#'+o.id}</span>
              <span style={{fontWeight:600,color:C.text,flex:1}}>{o.client_name}</span>
              <span style={{color:C.muted}}>{fmtDate(o.from_date)} → {fmtDate(o.to_date)}</span>
              <span>Collected <strong style={{color:C.green}}>{fmt(o.amount_paid)}</strong></span>
              <button style={S.btn('danger')} onClick={()=>setRefundModal(o)}>↩ Refund</button>
            </div>
          ))}
        </div>
      )}

      {/* Toolbar */}
      <div style={{display:'flex',gap:10,marginBottom:14,flexWrap:'wrap',alignItems:'center'}}>
        <input style={{...S.input,width:190}} placeholder="🔍 Client / order #..." value={search} onChange={e=>setSearch(e.target.value)} />
        <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
          {[['all','All'],['due','Outstanding'],['overdue',`Overdue${stats.overdueCount>0?` (${stats.overdueCount})`:''}`,],['paid','Paid'],['deposit','Deposit due']].map(([v,l])=>(
            <button key={v} onClick={()=>setFilter(v)}
              style={{...S.btn(''),padding:'6px 14px',borderRadius:20,background:filter===v?C.accent:'#2e3150',color:filter===v?'#fff':C.muted,fontSize:12}}>
              {l}
            </button>
          ))}
        </div>
        <div style={{display:'flex',gap:6,alignItems:'center',marginLeft:'auto',flexWrap:'wrap'}}>
          <input type="date" style={{...S.input,fontSize:12,padding:'6px 10px'}} value={dateRange.from} onChange={e=>setDateRange(p=>({...p,from:e.target.value}))}/>
          <span style={{color:C.muted,fontSize:12}}>→</span>
          <input type="date" style={{...S.input,fontSize:12,padding:'6px 10px'}} value={dateRange.to} onChange={e=>setDateRange(p=>({...p,to:e.target.value}))}/>
          {(dateRange.from||dateRange.to)&&<button style={S.btn('ghost')} onClick={()=>setDateRange({from:'',to:''})}>✕</button>}
        </div>
      </div>

      {/* Table */}
      <div style={{...S.card,padding:0,overflow:'hidden'}}>
        <div style={{overflowX:'auto'}}>
          <table style={{width:'100%',borderCollapse:'collapse'}}>
            <thead>
              <tr style={{background:'#181928'}}>
                {['','#','Client','Dates','Total','Collected','Balance','Status','Actions'].map(h=>(
                  <th key={h} style={S.th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.length===0&&(
                <tr><td colSpan={8} style={{...S.td,textAlign:'center',padding:40,color:C.muted}}>
                  {search||filter!=='all'?'No orders match your filter':'No orders yet'}
                </td></tr>
              )}
              {filtered.map(o=>{
                const balance  = Math.max(0,Number(o.total||0)-Number(o.amount_paid||0));
                const isExpand = expandId===o.id;
                const isOverdue= ['payment_due','partially_paid'].includes(o.payment_status)&&o.to_date&&o.to_date<today();
                return (
                  <React.Fragment key={o.id}>
                    <tr
                      style={{cursor:'pointer',background:isExpand?'#080812':isOverdue?'#1a0a00':'transparent'}}
                      onMouseEnter={e=>{if(!isExpand)e.currentTarget.style.background=isOverdue?'#220d00':'#2a2d40';}}
                      onMouseLeave={e=>{if(!isExpand)e.currentTarget.style.background=isOverdue?'#1a0a00':'transparent';}}
                      onClick={()=>handleExpand(o.id)}
                    >
                      <td style={{...S.td,width:32}} onClick={e=>e.stopPropagation()}>
                        <input type="checkbox" checked={selected.has(o.id)} onChange={e=>{setSelected(p=>{const n=new Set(p);e.target.checked?n.add(o.id):n.delete(o.id);return n;});}}
                          style={{cursor:'pointer',accentColor:C.accent}} />
                      </td>
                      <td style={S.td}>
                        <span style={{fontSize:11,color:C.muted}}>#{o.id}</span>
                        {isOverdue&&<span style={{marginLeft:4,fontSize:9,background:'#7f1d1d',color:'#fca5a5',padding:'1px 5px',borderRadius:4,fontWeight:700}}>OVERDUE</span>}
                      </td>
                      <td style={S.td}>
                        <div style={{fontWeight:700,color:C.text}}>{o.client_name||'—'}</div>
                        <div style={{fontSize:11,color:C.muted}}>{o.client_email||''}</div>
                      </td>
                      <td style={S.td}><span style={{fontSize:12,color:'#94a3b8'}}>{fmtDate(o.from_date)} → {fmtDate(o.to_date)}</span></td>
                      <td style={S.td}>
                        <span style={{fontWeight:700}}>{fmt(o.total)}</span>
                        {Number(o.tax_amount||0)>0&&<div style={{fontSize:10,color:C.muted}}>incl. {fmt(o.tax_amount)} tax</div>}
                      </td>
                      <td style={S.td}><span style={{color:C.green}}>{fmt(o.amount_paid)}</span></td>
                      <td style={S.td}>
                        <span style={{color:balance>0?C.amber:C.green,fontWeight:balance>0?700:400}}>
                          {balance>0?fmt(balance):'✓ Paid'}
                        </span>
                      </td>
                      <td style={S.td}><span style={S.pill(o.payment_status)}>{PAYMENT_LABEL[o.payment_status]||o.payment_status}</span></td>
                      <td style={S.td} onClick={e=>e.stopPropagation()}>
                        <div style={{display:'flex',gap:4,flexWrap:'wrap'}}>
                          {balance>0&&o.status!=='cancelled'&&<button style={S.btn('green')} onClick={()=>setCollectModal(o)}>💰 Collect</button>}
                          {balance>0&&o.status!=='cancelled'&&(o.client_phone||o.client_email)&&<button style={S.btn('orange')} onClick={()=>setPayLinkModal(o)}>🔗 Link</button>}
                          {Number(o.amount_paid||0)>0&&<button style={S.btn('sm')} onClick={()=>setRefundModal(o)}>↩</button>}
                        </div>
                      </td>
                    </tr>

                    {/* Expanded */}
                    {isExpand&&(
                      <tr style={{background:'#06070d'}}>
                        <td colSpan={8} style={{padding:'18px 20px 20px',borderBottom:`1px solid ${C.border}`}}>
                          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:20}}>
                            {/* Left: payment summary */}
                            <div>
                              <div style={{fontSize:11,color:C.muted,textTransform:'uppercase',letterSpacing:'.5px',marginBottom:10}}>Payment Summary</div>
                              <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:10}}>
                                {[
                                  ...taxLines(o).map(r => [r.label, r.kind === 'discount' ? '-' + fmt(-r.value) : fmt(r.value)]),
                                  ['Deposit',  o.deposit_amount?fmt(o.deposit_amount):'—'],
                                  ['Paid',     fmt(o.amount_paid)],
                                  ['Balance',  fmt(Math.max(0,Number(o.total||0)-Number(o.amount_paid||0)))],
                                ].map(([lbl,val])=>(
                                  <div key={lbl} style={{background:C.card,borderRadius:8,padding:'10px 14px'}}>
                                    <div style={{fontSize:10,color:C.muted,textTransform:'uppercase',letterSpacing:'.5px',marginBottom:4}}>{lbl}</div>
                                    <div style={{fontSize:14,fontWeight:700,color:C.text}}>{val}</div>
                                  </div>
                                ))}
                              </div>
                              {o.payment_link_url&&(
                                <div style={{padding:'8px 12px',background:'#0a1a3b',borderRadius:8,fontSize:12,color:C.blue}}>
                                  🔗 Link sent {fmtTime(o.payment_link_sent_at)} · <a href={o.payment_link_url} target="_blank" rel="noreferrer" style={{color:C.accent}}>Open</a>
                                </div>
                              )}
                            </div>

                            {/* Right: ledger */}
                            <div>
                              <div style={{fontSize:11,color:C.muted,textTransform:'uppercase',letterSpacing:'.5px',marginBottom:10,display:'flex',justifyContent:'space-between',alignItems:'center'}}>
                                <span>Payment History</span>
                                <button onClick={()=>refreshLedger(o.id)} style={{...S.btn('ghost'),fontSize:10,padding:'2px 6px'}}>↻ Refresh</button>
                              </div>
                              {loadingL[o.id]?(
                                <div style={{color:C.muted,fontSize:12,padding:10}}>Loading...</div>
                              ):(ledger[o.id]||[]).length===0?(
                                <div style={{color:C.muted,fontSize:12,fontStyle:'italic'}}>No payment records yet.</div>
                              ):(
                                <div style={{display:'flex',flexDirection:'column',gap:6}}>
                                  {(ledger[o.id]||[]).map((e,i)=>(
                                    <div key={e.id||i} style={{display:'flex',alignItems:'center',gap:10,padding:'8px 12px',background:C.card,borderRadius:8}}>
                                      <span style={{fontSize:15}}>{METHOD_ICON[e.method]||'💰'}</span>
                                      <div style={{flex:1,minWidth:0}}>
                                        <div style={{fontSize:12,fontWeight:700,color:e.direction==='refund'?C.red:C.green}}>
                                          {e.direction==='refund'?'−':'+'}{fmt(e.amount)}
                                          <span style={{fontSize:10,fontWeight:400,color:C.muted,marginLeft:6,textTransform:'capitalize'}}>{e.method} · {e.direction}</span>
                                        </div>
                                        {e.note&&<div style={{fontSize:11,color:C.muted,marginTop:2}}>{e.note}</div>}
                                      </div>
                                      <div style={{fontSize:10,color:C.muted,whiteSpace:'nowrap'}}>{fmtTime(e.created_at)}</div>
                                    </div>
                                  ))}
                                  <div style={{textAlign:'right',fontSize:12,color:C.muted,padding:'4px 12px'}}>
                                    Net: <strong style={{color:C.green}}>{fmt((ledger[o.id]||[]).reduce((s,e)=>s+(e.direction==='refund'?-Number(e.amount):Number(e.amount)),0))}</strong>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
        {filtered.length>0&&(
          <div style={{padding:'12px 20px',borderTop:`1px solid ${C.border}`,display:'flex',gap:24,justifyContent:'flex-end',fontSize:13,flexWrap:'wrap'}}>
            <span style={{color:C.muted}}>{filtered.length} orders</span>
            <span>Billed: <strong style={{color:C.text}}>{fmt(filtered.reduce((s,o)=>s+Number(o.total||0),0))}</strong></span>
            <span>Collected: <strong style={{color:C.green}}>{fmt(filtered.reduce((s,o)=>s+Number(o.amount_paid||0),0))}</strong></span>
            <span>Outstanding: <strong style={{color:C.amber}}>{fmt(filtered.reduce((s,o)=>s+Math.max(0,Number(o.total||0)-Number(o.amount_paid||0)),0))}</strong></span>
          </div>
        )}
      </div>

      {collectModal&&<CollectModal order={collectModal} onClose={()=>setCollectModal(null)} onSuccess={()=>{setCollectModal(null);onOrderUpdate?.();clearLedgerCache(collectModal.id);}}/>}
      {refundModal &&<RefundDialog order={refundModal} onClose={()=>setRefundModal(null)} onDone={()=>{setRefundModal(null); onOrderUpdate?.();clearLedgerCache(refundModal.id);}} />}
      {payLinkModal&&<PayLinkModal order={payLinkModal} onClose={()=>setPayLinkModal(null)} onSuccess={()=>{setPayLinkModal(null);onOrderUpdate?.();}}/>}
      </>)}
    </div>
  );
}

/* ── COLLECT MODAL ── */
function CollectModal({ order, onClose, onSuccess }) {
  const balance = Math.max(0, Number(order.total||0) - Number(order.amount_paid||0));
  const [amount, setAmount]       = useState(balance.toFixed(2));
  const [method, setMethod]       = useState('cash');
  const [note,   setNote]         = useState('');
  const [receipt,setReceipt]      = useState(!!(order.client_phone||order.client_email));
  const [saving, setSaving]       = useState(false);
  const overpay = Number(amount) > balance + 0.01;

  const submit = async () => {
    const amt = parseFloat(amount);
    if (!amt||amt<=0) return alert('Enter a valid amount');
    setSaving(true);
    try { await axios.post(`/api/rentals/orders/${order.id}/collect`,{amount:amt,method,note,direction:'payment',send_receipt:receipt,allow_overpay:overpay}); onSuccess(); }
    catch(e) { alert('Error: '+(e.response?.data?.error||e.message)); }
    setSaving(false);
  };

  return (
    <Modal title="💰 Collect Payment" subtitle={`Order #${order.id} · ${order.client_name}`} onClose={onClose}>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8,marginBottom:14}}>
        {[['Total',fmt(order.total),C.text],['Collected',fmt(order.amount_paid),C.green],['Balance',fmt(balance),C.amber]].map(([l,v,c])=>(
          <div key={l} style={{background:'#181928',borderRadius:8,padding:'10px 12px',textAlign:'center'}}>
            <div style={{fontSize:10,color:C.muted,marginBottom:3}}>{l}</div>
            <div style={{fontSize:15,fontWeight:800,color:c}}>{v}</div>
          </div>
        ))}
      </div>
      <label style={S.lbl}>Amount</label>
      <input style={{...S.inp,fontSize:22,fontWeight:800,color:overpay?C.amber:C.green,borderColor:overpay?C.amber:'#3a3f60'}}
        type="number" min="0" step="0.01" value={amount} onChange={e=>setAmount(e.target.value)} autoFocus/>
      {overpay&&<div style={{fontSize:11,color:C.amber,marginTop:4}}>⚠️ Over balance — will mark overpaid</div>}
      <div style={{display:'flex',gap:4,marginTop:6}}>
        {[25,50,75,100].map(p=>(
          <button key={p} onClick={()=>setAmount((balance*p/100).toFixed(2))} style={{...S.btn('sm'),flex:1,fontSize:11}}>{p}%</button>
        ))}
      </div>
      <label style={S.lbl}>Method</label>
      <div style={{display:'flex',gap:5,flexWrap:'wrap'}}>
        {['cash','card','check','venmo','zelle','other'].map(m=>(
          <button key={m} onClick={()=>setMethod(m)}
            style={{padding:'6px 12px',borderRadius:6,border:`1px solid ${method===m?C.accent:'#3a3f60'}`,background:method===m?'rgba(249,115,22,0.15)':'transparent',color:method===m?C.accent:C.muted,fontSize:11,fontWeight:700,cursor:'pointer',textTransform:'capitalize'}}>
            {METHOD_ICON[m]} {m}
          </button>
        ))}
      </div>
      <label style={S.lbl}>Note</label>
      <input style={S.inp} value={note} onChange={e=>setNote(e.target.value)} placeholder="e.g. paid at pickup"/>
      {(order.client_phone||order.client_email)&&(
        <label style={{display:'flex',alignItems:'center',gap:8,marginTop:14,cursor:'pointer',fontSize:13,color:C.text}}>
          <input type="checkbox" checked={receipt} onChange={e=>setReceipt(e.target.checked)}/>
          Send receipt {order.client_phone?'via SMS':'via email'}
        </label>
      )}
      <ModalFooter onClose={onClose} onSave={submit} saving={saving} label="✓ Record Payment"/>
    </Modal>
  );
}

/* ── REFUND MODAL ── */
// RefundModal moved to components/orderPipeline/RefundDialog.jsx (one refund dialog in the app).

function PayLinkModal({ order, onClose, onSuccess }) {
  const balance    = Math.max(0,Number(order.total||0)-Number(order.amount_paid||0));
  const depositAmt = Math.min(Number(order.deposit_amount||0)||balance*0.25, balance);
  const [payType,  setPayType]  = useState('balance');
  const [sendSms,  setSendSms]  = useState(!!(order.client_phone));
  const [sendEmail,setSendEmail]= useState(!!(order.client_email));
  const [saving,   setSaving]   = useState(false);
  const [generated,setGenerated]= useState(null);
  const amount = payType==='deposit' ? depositAmt : balance;

  const generate = async () => {
    setSaving(true);
    try {
      const r = await axios.post(`/api/rentals/orders/${order.id}/payment-link`,{payment_type:payType,send_sms:sendSms,send_email:sendEmail});
      setGenerated(r.data);
      onSuccess();
    } catch(e) { alert('Error: '+(e.response?.data?.error||e.message)); }
    setSaving(false);
  };

  return (
    <Modal title="🔗 Payment Link" subtitle={`Order #${order.id} · ${order.client_name}`} onClose={onClose}>
      {generated ? (
        <div>
          <div style={{padding:'14px',background:'#0a1a0a',borderRadius:8,marginBottom:12}}>
            <div style={{fontSize:13,color:C.green,fontWeight:700,marginBottom:8}}>✓ Link created — ${Number(generated.amount).toFixed(2)}</div>
            <input readOnly value={generated.url} style={{...S.inp,fontSize:11,color:C.blue}} onClick={e=>e.target.select()}/>
          </div>
          <button onClick={()=>navigator.clipboard.writeText(generated.url)} style={{...S.btn(''),width:'100%',marginBottom:8}}>📋 Copy Link</button>
          <button onClick={onClose} style={{...S.btn('ghost'),width:'100%'}}>Done</button>
        </div>
      ) : (
        <>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr 1fr',gap:8,marginBottom:14}}>
            {[['Total',fmt(order.total)],['Balance',fmt(balance)],['Deposit',fmt(depositAmt)]].map(([l,v])=>(
              <div key={l} style={{background:'#181928',borderRadius:8,padding:'10px 12px',textAlign:'center'}}>
                <div style={{fontSize:10,color:C.muted,marginBottom:3}}>{l}</div>
                <div style={{fontSize:14,fontWeight:800,color:C.text}}>{v}</div>
              </div>
            ))}
          </div>
          <label style={S.lbl}>Charge Amount</label>
          <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:8,marginBottom:12}}>
            {[['balance',`Full Balance ${fmt(balance)}`],['deposit',`Deposit Only ${fmt(depositAmt)}`]].map(([v,l])=>(
              <button key={v} onClick={()=>setPayType(v)}
                style={{padding:'12px',borderRadius:8,border:`2px solid ${payType===v?C.accent:'#3a3f60'}`,background:payType===v?'rgba(249,115,22,0.1)':'transparent',color:payType===v?C.accent:C.muted,fontSize:12,fontWeight:700,cursor:'pointer'}}>
                {l}
              </button>
            ))}
          </div>
          <label style={S.lbl}>Send Via</label>
          <div style={{display:'flex',flexDirection:'column',gap:8}}>
            {order.client_phone&&(
              <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',fontSize:13,color:C.text}}>
                <input type="checkbox" checked={sendSms} onChange={e=>setSendSms(e.target.checked)}/> SMS to {order.client_phone}
              </label>
            )}
            {order.client_email&&(
              <label style={{display:'flex',alignItems:'center',gap:8,cursor:'pointer',fontSize:13,color:C.text}}>
                <input type="checkbox" checked={sendEmail} onChange={e=>setSendEmail(e.target.checked)}/> Email to {order.client_email}
              </label>
            )}
            {!order.client_phone&&!order.client_email&&(
              <div style={{fontSize:12,color:C.amber}}>⚠️ No contact info — copy link to share manually.</div>
            )}
          </div>
          <ModalFooter onClose={onClose} onSave={generate} saving={saving} label={`🔗 Generate Link ($${amount.toFixed(2)})`}/>
        </>
      )}
    </Modal>
  );
}

/* ── SHARED MODAL ── */
function Modal({ title, subtitle, onClose, children }) {
  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.75)',zIndex:4000,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}
      onClick={e=>e.target===e.currentTarget&&onClose()}>
      <div style={{background:'#252838',border:'1px solid #2e3150',borderRadius:14,width:'100%',maxWidth:460,boxShadow:'0 20px 60px rgba(0,0,0,0.5)',maxHeight:'90vh',overflowY:'auto'}}>
        <div style={{padding:'16px 20px',borderBottom:'1px solid #2e3150',display:'flex',justifyContent:'space-between',alignItems:'flex-start'}}>
          <div>
            <div style={{fontWeight:800,fontSize:15,color:C.text}}>{title}</div>
            {subtitle&&<div style={{fontSize:11,color:C.muted,marginTop:2}}>{subtitle}</div>}
          </div>
          <button onClick={onClose} style={{background:'none',border:'none',color:C.muted,cursor:'pointer',fontSize:18,lineHeight:1}}>✕</button>
        </div>
        <div style={{padding:'20px'}}>{children}</div>
      </div>
    </div>
  );
}

function ModalFooter({ onClose, onSave, saving, label, danger }) {
  return (
    <div style={{display:'flex',gap:10,marginTop:20}}>
      <button onClick={onClose} style={{flex:1,padding:'11px',borderRadius:8,border:'1px solid #3a3f60',background:'transparent',color:C.muted,cursor:'pointer',fontSize:13}}>Cancel</button>
      <button onClick={onSave} disabled={saving}
        style={{flex:2,padding:'11px',borderRadius:8,border:'none',fontWeight:800,cursor:'pointer',fontSize:14,background:danger?'#7f1d1d':'#14532d',color:danger?C.red:C.green,opacity:saving?0.6:1}}>
        {saving?'⏳ Saving...':label}
      </button>
    </div>
  );
}
